import { supabase } from "@/integrations/supabase/client";

/**
 * Checks if a URL points to a Supabase public storage object
 */
export function isSupabasePublicStorageUrl(url?: string | null): boolean {
  if (!url) return false;
  return url.includes('/storage/v1/object/public/');
}

/**
 * Extracts bucket and path from a Supabase public URL
 * Returns { bucket: 'image_uploads', path: 'fund/123/logo.png' }
 */
export function parseSupabaseUrl(publicUrl: string): { bucket: string; path: string } | null {
  if (!isSupabasePublicStorageUrl(publicUrl)) return null;
  
  const marker = '/storage/v1/object/public/';
  const idx = publicUrl.indexOf(marker);
  if (idx === -1) return null;
  
  const fullPath = publicUrl.substring(idx + marker.length);
  const parts = fullPath.split('/');
  if (parts.length < 2) return null;
  
  const bucket = parts[0];
  const path = parts.slice(1).join('/');
  
  return { bucket, path };
}

/**
 * Creates a properly formatted Supabase transform URL using the official SDK
 * This replaces manual URL building and should fix 400 errors
 */
export function getRenderUrlFromPublicUrl(
  publicUrl: string,
  params: { width?: number; height?: number; quality?: number; resize?: 'cover' | 'contain' | 'fill'; gravity?: string; format?: string } = {}
): string {
  // If not a Supabase public URL, return as-is
  if (!isSupabasePublicStorageUrl(publicUrl)) return publicUrl;

  const parsed = parseSupabaseUrl(publicUrl);
  if (!parsed) return publicUrl;

  const { width, height, quality = 80, resize, gravity, format } = params;

  try {
    // Create minimal transform object - only include defined values
    const transform: any = {};
    
    // Only add width if specified and valid
    if (width && width > 0) {
      transform.width = Math.max(64, Math.min(2048, Math.floor(width)));
    }
    
    // Only add height if specified and valid
    if (height && height > 0) {
      transform.height = Math.max(64, Math.min(2048, Math.floor(height)));
    }
    
    // Always include quality for better compression
    if (quality && quality > 0) {
      transform.quality = Math.max(20, Math.min(95, Math.floor(quality)));
    }
    
    // Add resize mode if both width and height are specified
    if (resize && transform.width && transform.height) {
      transform.resize = resize;
    }
    
    // Add gravity for smart cropping
    if (gravity && resize) {
      transform.gravity = gravity;
    }
    
    // Add format for WebP conversion
    if (format) {
      transform.format = format;
    }

    // Only apply transforms if we have meaningful parameters
    if (Object.keys(transform).length === 0) {
      return publicUrl;
    }

    const { data } = supabase.storage
      .from(parsed.bucket)
      .getPublicUrl(parsed.path, { transform });

    // Add cache control hint by rounding timestamp to nearest hour
    // This creates stable URLs that browsers can cache for up to 1 hour
    const cacheHint = Math.floor(Date.now() / (60 * 60 * 1000));
    const urlWithCache = `${data.publicUrl}&t=${cacheHint}`;

    console.log('[getRenderUrlFromPublicUrl] Generated transform URL:', {
      original: publicUrl,
      transformed: urlWithCache,
      transform,
      bucket: parsed.bucket,
      path: parsed.path
    });

    return urlWithCache;
  } catch (error) {
    console.warn('[getRenderUrlFromPublicUrl] Failed to generate transform URL:', error);
    return publicUrl; // Fallback to original URL
  }
}

/**
 * Builds a srcset string using Supabase render URLs for given widths
 */
export function buildSrcSetFromPublicUrl(
  publicUrl: string,
  widths: number[] = [256, 384, 512, 768, 1024],
  quality = 80
): string | undefined {
  if (!isSupabasePublicStorageUrl(publicUrl)) return undefined;
  const uniqueSorted = Array.from(new Set(widths.filter(w => w > 0))).sort((a, b) => a - b);
  const entries = uniqueSorted.map(w => `${getRenderUrlFromPublicUrl(publicUrl, { width: w, quality })} ${w}w`);
  return entries.join(', ');
}

/**
 * Extracts the bucket/path portion from a public URL
 * Returns e.g., "image_uploads/fund/123/logo.png"
 */
export function getObjectPathFromPublicUrl(publicUrl: string): string | null {
  if (!isSupabasePublicStorageUrl(publicUrl)) return null;
  const marker = '/storage/v1/object/public/';
  const idx = publicUrl.indexOf(marker);
  if (idx === -1) return null;
  return publicUrl.substring(idx + marker.length);
}